import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

interface AddUpcomingButtonProps {
  onClick: () => void;
}

const AddUpcomingButton: React.FC<AddUpcomingButtonProps> = ({ onClick }) => {
  const [isFloating, setIsFloating] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const buttonElement = buttonRef.current;
    if (!buttonElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Float when any part of button is hidden (not 100% visible)
        if (entry.intersectionRatio < 1.0) {
          setIsFloating(true);
        } else {
          setIsFloating(false);
        }
      },
      {
        threshold: 1.0, // Trigger when button is no longer 100% visible
        rootMargin: '-64px 0px 0px 0px', // Account for fixed AppBar (64px is standard Toolbar height)
      }
    );

    observer.observe(buttonElement);

    return () => {
      observer.unobserve(buttonElement);
    };
  }, []);

  return (
    <Box
      ref={buttonRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        my: 3,
        // Reserve space for the inline button to prevent layout shift
        minHeight: 56,
      }}
    >
      <Tooltip title="Add Upcoming" arrow>
        <IconButton
          onClick={onClick}
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: isFloating ? 6 : 2,
            // Conditional positioning with smooth transition
            position: isFloating ? 'fixed' : 'relative',
            bottom: isFloating ? 24 : 'auto',
            right: isFloating ? 24 : 'auto',
            zIndex: isFloating ? 1000 : 1,
            // Smooth animations
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isFloating ? 1 : 1,
            transform: isFloating ? 'scale(1)' : 'scale(1)',
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'scale(1.1)',
              boxShadow: 8,
            },
          }}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AddUpcomingButton;
